/**
 * WS-NAV — pure workspace resolution. No React, no storage, no router: every
 * rule in the brief is a function of (pathname, access, last-used id) so the
 * routing rules can be proven at the unit tier.
 *
 * The four rules, verbatim from the brief:
 *   1. Workspaces are **role-filtered first**, workspace-scoped second.
 *   2. **Single-workspace users skip Home** — a pure pilot lands in Field Data.
 *   3. Returning users land in their **last-used workspace**.
 *   4. **Deep links auto-activate the owning workspace** — no redirects, no
 *      path changes, no dead ends.
 */
import { HOME_PATH, WORKSPACES, preferredWorkspaceId } from "./workspaces.js";
import type {
  Workspace,
  WorkspaceAccess,
  WorkspaceId,
  WorkspaceNavItem,
} from "./types.js";

export { HOME_PATH };

/** True when the caller may be *offered* `item` (never a security boundary). */
function itemVisible(item: WorkspaceNavItem, access: WorkspaceAccess): boolean {
  if (item.requiresContactRead === true) return access.canReadContacts;
  if (item.requiresTenantAdmin === true) return access.isTenantAdmin;
  return true;
}

/** The nav items of `workspace` the caller may see, in declaration order. */
export function visibleItems(
  workspace: Workspace,
  access: WorkspaceAccess,
): readonly WorkspaceNavItem[] {
  return workspace.items.filter((item) => itemVisible(item, access));
}

/**
 * The workspaces offered to the caller.
 *
 * Two filters, both "offer" filters: the workspace's `roles` list, and the
 * emptiness rule — a workspace whose every item is gated away for this role is
 * not a workspace, it's an empty room (this is what keeps `Administration` out
 * of a pilot's switcher while leaving Contacts reachable for back_office).
 */
export function visibleWorkspaces(
  access: WorkspaceAccess,
): readonly Workspace[] {
  if (access.role === null) return [];
  const role = access.role;
  return WORKSPACES.filter(
    (w) => w.roles.includes(role) && visibleItems(w, access).length > 0,
  );
}

/** Where a workspace opens for THIS caller — its start page, or, if that item
 *  is gated away, its first visible item. */
export function startPathFor(
  workspace: Workspace,
  access: WorkspaceAccess,
): string {
  const items = visibleItems(workspace, access);
  const startIsVisible = items.some(
    (i) => i.to === workspace.startPath && i.enabled !== false,
  );
  if (startIsVisible) return workspace.startPath;
  const firstRoutable = items.find(
    (i) => i.enabled !== false && i.to !== undefined,
  );
  return firstRoutable?.to ?? HOME_PATH;
}

function ownsPath(workspace: Workspace, pathname: string): boolean {
  const owns = (p: string): boolean =>
    pathname === p || (p !== "/" && pathname.startsWith(`${p}/`));
  if (workspace.items.some((i) => i.to !== undefined && owns(i.to)))
    return true;
  return (workspace.ownedPathPrefixes ?? []).some(owns);
}

/**
 * Every workspace that claims `pathname`. Usually zero or one; exactly two for
 * `/defect-catalogues`, which Analysis (view) and Administration (manage) both
 * own — "same route, two doors".
 */
export function workspacesForPath(pathname: string): readonly Workspace[] {
  return WORKSPACES.filter((w) => ownsPath(w, pathname));
}

/**
 * Rule 4 — the workspace `pathname` puts the user IN, or `null` when no
 * visible workspace owns it (`/account/settings`, or `/` for an analyst).
 *
 * Precedence among the workspaces that own the path:
 *
 *   1. **Explicit intent** — the user picked this workspace from the switcher,
 *      a Home card or a shortcut, and the choice travels with the navigation
 *      (router location state). A destination can be owned by more than one
 *      workspace, and then the URL simply cannot say which one was meant.
 *   2. **Stickiness** — otherwise the workspace already active keeps it, so
 *      opening a shared route (`/defect-catalogues`) from Administration
 *      doesn't teleport the user to Analysis, and a PM working through Projects
 *      isn't yanked into Analysis by its own Data Acquisitions entry.
 *   3. **Whose start page is this?** — a workspace that OPENS on the route owns
 *      it more than one that merely lists it. Without this, a cold deep link to
 *      `/data-acquisitions` (Analysis's start page, and a Projects nav entry)
 *      resolved by config order, i.e. to Projects.
 *   4. Config order, as a last resort.
 *
 * This is the value worth REMEMBERING — `null` means "the user didn't go
 * anywhere in particular", which must not overwrite where they were last.
 */
export function resolveOwningWorkspaceId(args: {
  readonly pathname: string;
  readonly access: WorkspaceAccess;
  readonly currentId: WorkspaceId | null;
  /** From `location.state` — see `navigateToWorkspace`. */
  readonly intentId?: WorkspaceId | null;
}): WorkspaceId | null {
  const { pathname, access, currentId, intentId = null } = args;
  const visible = visibleWorkspaces(access);
  if (visible.length === 0) return null;

  const owners = workspacesForPath(pathname).filter((w) =>
    visible.some((v) => v.id === w.id),
  );
  if (owners.length === 0) return null;
  if (intentId !== null && owners.some((w) => w.id === intentId)) {
    return intentId;
  }
  if (currentId !== null && owners.some((w) => w.id === currentId)) {
    return currentId;
  }
  const opensHere = owners.find((w) => w.startPath === pathname);
  if (opensHere) return opensHere.id;
  return owners[0]?.id ?? null;
}

/**
 * The workspace the chrome should SHOW. Falls back past the owning answer so
 * the switcher and tree are never blank: the remembered workspace if it is
 * still offered, otherwise the caller's first one. The fallback is display
 * only — it is deliberately not written back as "last used", or a first-time
 * user would acquire a remembered workspace merely by loading a page and would
 * never see Home (landing rule 3 would fire on their first visit).
 */
export function resolveDisplayWorkspaceId(args: {
  readonly pathname: string;
  readonly access: WorkspaceAccess;
  readonly currentId: WorkspaceId | null;
}): WorkspaceId | null {
  const owning = resolveOwningWorkspaceId(args);
  if (owning !== null) return owning;

  const visible = visibleWorkspaces(args.access);
  const { currentId, access } = args;
  if (currentId !== null && visible.some((w) => w.id === currentId)) {
    return currentId;
  }
  // Doc §1 "Default landing for": an analyst sitting on a route no workspace
  // owns should see the Analysis tree, not whichever workspace happens to be
  // first in the config.
  const preferred =
    access.role === null ? null : preferredWorkspaceId(access.role);
  if (preferred !== null && visible.some((w) => w.id === preferred)) {
    return preferred;
  }
  return visible[0]?.id ?? null;
}

/**
 * Rules 2 + 3 — where the app should open after sign-in.
 *
 *   one workspace           → straight into it (skip Home)
 *   remembered workspace    → its start page
 *   otherwise               → Home
 */
export function resolveLandingPath(args: {
  readonly access: WorkspaceAccess;
  readonly lastUsedId: WorkspaceId | null;
}): string {
  const visible = visibleWorkspaces(args.access);
  if (visible.length === 0) return HOME_PATH;

  const only = visible.length === 1 ? visible[0] : undefined;
  if (only) return startPathFor(only, args.access);

  const remembered = visible.find((w) => w.id === args.lastUsedId);
  if (remembered) return startPathFor(remembered, args.access);

  return HOME_PATH;
}
